import { IconButton, MenuItem, MenuSeparator } from '@affine/component';
import {
  CATEGORY_DIVIDER_ACTION_LOCK_EVENT,
  CATEGORY_DIVIDER_KEEP_ACTIONS_ACTIVE_ATTR,
} from '@affine/core/modules/app-sidebar/views';
import { useI18n } from '@affine/i18n';
import {
  ArrowDownSmallIcon,
  ArrowUpSmallIcon,
  DeleteIcon,
  EditIcon,
  MoreHorizontalIcon,
  SettingsIcon,
  SortDownIcon,
} from '@blocksuite/icons/rc';
import { useCallback, useEffect, useRef, useState } from 'react';

import { StreamLainMenu } from '../../../../components/dropdown-panel-chrome/radix-menu';
import { StreamLainMenuSub } from '../../../../components/dropdown-panel-chrome/radix-menu-sub';
import {
  SIDEBAR_SORT_MODES,
  type SidebarSortMode,
} from './sidebar-sort-options';

const KEEP_AREA_ACTIONS_ACTIVE_PROPS = {
  [CATEGORY_DIVIDER_KEEP_ACTIONS_ACTIVE_ATTR]: 'true',
} as const;

export type SidebarAreaReorderActions = {
  canMoveDown: boolean;
  canMoveUp: boolean;
  onMoveDown: () => void;
  onMoveUp: () => void;
};

export type SidebarAreaOptionsMenuProps = {
  managePermissions?: () => void;
  rename?: () => void;
  reorderActions?: SidebarAreaReorderActions;
  sortMode?: SidebarSortMode;
  trash?: () => void;
  onSortModeChange?: (mode: SidebarSortMode) => void;
  testId: string;
};

export function SidebarAreaOptionsMenu({
  managePermissions,
  rename,
  reorderActions,
  sortMode,
  onSortModeChange,
  trash,
  testId,
}: SidebarAreaOptionsMenuProps) {
  const t = useI18n();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const hasSortOptions = sortMode !== undefined && onSortModeChange;
  const sortLabels: Record<SidebarSortMode, string> = {
    manual: t['com.streamlain.rootAppSidebar.area.sort.manual'](),
    alphabetical: t['com.streamlain.rootAppSidebar.area.sort.alphabetical'](),
    'folders-first':
      t['com.streamlain.rootAppSidebar.area.sort.folders-first'](),
    'docs-first': t['com.streamlain.rootAppSidebar.area.sort.docs-first'](),
  };
  const dispatchActionLock = useCallback((active: boolean) => {
    triggerRef.current?.dispatchEvent(
      new CustomEvent(CATEGORY_DIVIDER_ACTION_LOCK_EVENT, {
        bubbles: true,
        detail: { active },
      })
    );
  }, []);
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      dispatchActionLock(nextOpen);
    },
    [dispatchActionLock]
  );

  useEffect(
    () => () => {
      dispatchActionLock(false);
    },
    [dispatchActionLock]
  );

  if (
    !hasSortOptions &&
    !reorderActions &&
    !managePermissions &&
    !rename &&
    !trash
  ) {
    return null;
  }

  return (
    <StreamLainMenu
      triggerRef={triggerRef}
      rootOptions={{ open, onOpenChange: handleOpenChange }}
      contentOptions={{
        side: 'right',
        align: 'center',
      }}
      items={
        <>
          {rename ? (
            <MenuItem
              data-testid={`${testId}-rename`}
              prefixIcon={<EditIcon />}
              onSelect={rename}
            >
              {t['com.streamlain.menu.rename']()}
            </MenuItem>
          ) : null}
          {hasSortOptions ? (
            <StreamLainMenuSub
              triggerOptions={{
                prefixIcon: <SortDownIcon />,
                'data-testid': `${testId}-sort`,
              }}
              items={SIDEBAR_SORT_MODES.map(mode => (
                <MenuItem
                  key={mode}
                  data-testid={`${testId}-sort-${mode}`}
                  selected={sortMode === mode}
                  onSelect={() => onSortModeChange(mode)}
                >
                  {sortLabels[mode]}
                </MenuItem>
              ))}
            >
              {t['com.streamlain.explorer.display-menu.ordering']()}
            </StreamLainMenuSub>
          ) : null}
          {reorderActions ? (
            <>
              <MenuItem
                data-testid={`${testId}-move-up`}
                disabled={!reorderActions.canMoveUp}
                prefixIcon={<ArrowUpSmallIcon />}
                onSelect={reorderActions.onMoveUp}
              >
                {t['com.streamlain.rootAppSidebar.area.move-up']()}
              </MenuItem>
              <MenuItem
                data-testid={`${testId}-move-down`}
                disabled={!reorderActions.canMoveDown}
                prefixIcon={<ArrowDownSmallIcon />}
                onSelect={reorderActions.onMoveDown}
              >
                {t['com.streamlain.rootAppSidebar.area.move-down']()}
              </MenuItem>
            </>
          ) : null}
          {managePermissions ? (
            <MenuItem
              data-testid={`${testId}-manage-permissions`}
              prefixIcon={<SettingsIcon />}
              onSelect={managePermissions}
            >
              {t['com.streamlain.rootAppSidebar.teamspace.manage']()}
            </MenuItem>
          ) : null}
          {trash ? (
            <>
              <MenuSeparator />
              <MenuItem
                data-testid={`${testId}-trash`}
                type="danger"
                prefixIcon={<DeleteIcon />}
                onSelect={trash}
              >
                {t['com.streamlain.moveToTrash.title']()}
              </MenuItem>
            </>
          ) : null}
        </>
      }
    >
      <IconButton
        {...KEEP_AREA_ACTIONS_ACTIVE_PROPS}
        ref={triggerRef}
        data-testid={testId}
        size="16"
        tooltip={t['com.streamlain.rootAppSidebar.area.options']()}
      >
        <MoreHorizontalIcon />
      </IconButton>
    </StreamLainMenu>
  );
}
